import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClientProvider } from '@tanstack/react-query';

import { ToastProvider } from './hooks/useToast.tsx';
import { queryClient } from './lib/queryClient.ts';
import { initPostHog } from './lib/posthog.ts';
import store, { persistor } from './store.ts';
import { App } from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import './index.css';

// Initialize PostHog analytics
initPostHog();

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: `fairday-advertiser@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: true,
    }),
  ],
  beforeSend(event) {
    // Scrub sensitive headers
    if (event.request?.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
      delete event.request.headers['Cookie'];
      delete event.request.headers['cookie'];
    }

    // Scrub sensitive fields from request body
    if (event.request?.data) {
      const sensitiveFields = ['password', 'token', 'secret', 'ssn', 'credit_card', 'api_key', 'refresh_token', 'access_token'];
      try {
        const data = typeof event.request.data === 'string' ? JSON.parse(event.request.data) : event.request.data;
        for (const field of sensitiveFields) {
          if (data[field]) data[field] = '[FILTERED]';
        }
        event.request.data = data;
      } catch {
        // Not JSON, leave as-is
      }
    }

    return event;
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <ToastProvider>
                  <App />
                </ToastProvider>
              </QueryClientProvider>
            </BrowserRouter>
          </PersistGate>
        </Provider>
      </HelmetProvider>
    </ErrorBoundary>
  </StrictMode>,
);
